import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, Upload, message, Popconfirm, Tag, Dropdown, Alert, Space } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, CheckCircleOutlined, MoreOutlined, FileOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { MenuProps } from 'antd';
import { apiMethods, Version, App } from '../api';

const { TextArea } = Input;
const { Option } = Select;

const platforms = ['windows', 'macos', 'linux', 'android', 'ios', 'web'];
const architectures = ['amd64', 'arm64', 'x86', 'arm', 'universal'];

export default function Versions() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<Version[]>([]);
  const [app, setApp] = useState<App | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const [versionsData, appData] = await Promise.all([
        apiMethods.getVersions(Number(appId)),
        apiMethods.getApp(Number(appId)),
      ]);
      setVersions(versionsData.data.versions || []);
      setApp(appData.data.app);
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取版本列表失败');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    if (appId) {
      fetchVersions();
    }
  }, [appId, fetchVersions]);

  const handleDelete = async (id: number) => {
    try {
      await apiMethods.deleteVersion(id);
      message.success('删除成功');
      fetchVersions();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await apiMethods.setDefaultVersion(id);
      message.success('设置成功');
      fetchVersions();
    } catch (error: any) {
      message.error(error.response?.data?.error || '设置失败');
    }
  };

  const handleUpload = async () => {
    const version = form.getFieldValue('version');
    const platform = form.getFieldValue('platform');
    const architecture = form.getFieldValue('architecture');
    const changelog = form.getFieldValue('changelog');
    const isDefault = form.getFieldValue('is_default');

    if (!version) {
      message.error('请输入版本号');
      return;
    }
    if (fileList.length === 0) {
      message.error('请选择文件');
      return;
    }

    // 整合包类型自动使用 universal 平台和架构
    const finalPlatform = app?.type === 'modpack' ? 'universal' : platform;
    const finalArchitecture = app?.type === 'modpack' ? 'universal' : architecture;

    if (!finalPlatform) {
      message.error('请选择平台');
      return;
    }
    if (!finalArchitecture) {
      message.error('请选择架构');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      formData.append('version', version);
      formData.append('platform', finalPlatform);
      formData.append('architecture', finalArchitecture);
      formData.append('changelog', changelog || '');
      formData.append('is_default', isDefault ? 'true' : 'false');

      await apiMethods.createVersion(Number(appId), formData);
      message.success('上传成功');
      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchVersions();
    } catch (error: any) {
      message.error(error.response?.data?.error || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps: UploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // 移动端操作菜单
  const getActionMenu = (record: Version): MenuProps => ({
    items: [
      {
        key: 'viewFiles',
        label: '查看文件',
        onClick: () => navigate(`/versions/${record.id}/files`),
      },
      ...(record.is_default ? [] : [
        {
          key: 'setDefault',
          label: '设为默认',
          onClick: () => handleSetDefault(record.id),
        },
      ]),
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除',
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确定删除？',
            content: `确定要删除版本 "${record.version}" 吗？`,
            okText: '确定',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: () => handleDelete(record.id),
          });
        },
      },
    ],
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60, responsive: ['lg'] as ['lg'] },
    { title: '版本号', dataIndex: 'version', width: 100 },
    { title: '平台', dataIndex: 'platform', width: 100 },
    { title: '架构', dataIndex: 'architecture', width: 100, responsive: ['md'] as ['md'] },
    { title: '文件名', dataIndex: 'file_name', width: 180, ellipsis: true },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      width: 100,
      responsive: ['md'] as ['md'],
      render: (size: number) => formatFileSize(size),
    },
    { title: 'SHA256', dataIndex: 'checksum', responsive: ['xl'] as ['xl'], ellipsis: true },
    {
      title: '更新日志',
      dataIndex: 'changelog',
      width: 150,
      responsive: ['lg'] as ['lg'],
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'is_default',
      width: 100,
      render: (isDefault: boolean) =>
        isDefault ? <Tag icon={<CheckCircleOutlined />} color="success">默认</Tag> : null,
    },
    { title: '发布时间', dataIndex: 'released_at', width: 160, responsive: ['lg'] as ['lg'] },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: isMobile ? ('right' as const) : undefined,
      render: (_: any, record: Version) => {
        if (isMobile) {
          return (
            <Dropdown menu={getActionMenu(record)} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
        return (
          <>
            <Button
              type="link"
              icon={<FileOutlined />}
              onClick={() => navigate(`/versions/${record.id}/files`)}
            >
              查看文件
            </Button>
            {!record.is_default && (
              <Button type="link" onClick={() => handleSetDefault(record.id)}>
                设为默认
              </Button>
            )}
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" danger>
                删除
              </Button>
            </Popconfirm>
          </>
        );
      },
    },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/apps')}
        style={{ marginBottom: 16 }}
      >
        返回应用列表
      </Button>

      <Space style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 18, fontWeight: 500 }}>
          {app?.display_name}
        </span>
        {app?.type === 'modpack' && (
          <Tag color="purple">整合包</Tag>
        )}
        {app?.type === 'normal' && (
          <Tag color="blue">普通应用</Tag>
        )}
      </Space>

      <div style={{ marginBottom:16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
          上传新版本
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={versions}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="上传新版本"
        open={modalVisible}
        onOk={handleUpload}
        onCancel={() => setModalVisible(false)}
        confirmLoading={uploading}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { height: 'calc(100vh - 108px)', overflowY: 'auto' } } : undefined}
      >
        {app?.type === 'modpack' ? (
          <Alert
            message="整合包上传说明"
            description={
              <div>
                <div>• 上传 <strong>zip 文件</strong>，系统会自动解压并记录文件清单</div>
                <div>• 客户端支持<strong>增量更新</strong>，只下载变化的文件</div>
                <div>• zip 应包含 <strong>mods/</strong>、<strong>config/</strong> 等文件夹</div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            message="普通应用上传说明"
            description={
              <div>
                <div>• 上传 <strong>安装包文件</strong>（exe、msi、zip 等）</div>
                <div>• 客户端会下载完整文件进行更新</div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="version"
            label="版本号"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="例如: 1.0.0" />
          </Form.Item>
          {app?.type !== 'modpack' && (
            <>
              <Form.Item
                name="platform"
                label="平台"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select placeholder="请选择平台">
                  {platforms.map(p => (
                    <Option key={p} value={p}>{p}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="architecture"
                label="架构"
                rules={[{ required: true, message: '请选择架构' }]}
              >
                <Select placeholder="请选择架构">
                  {architectures.map(a => (
                    <Option key={a} value={a}>{a}</Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item
            name="file"
            label="文件"
            rules={[{ required: true, message: '请选择文件' }]}
            extra={fileList.length > 0 ? `已选择: ${fileList[0].name} (${formatFileSize(fileList[0].size)})` : '支持 .zip 文件，将自动解压'}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="changelog" label="更新日志">
            <TextArea rows={4} placeholder="输入更新日志..." />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked">
            <input type="checkbox" /> 设为默认版本
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
